import React from 'react';
import type { BilanImport, ModeEcriture, RenfortPropose } from '../../../lib/planningExcelImport/types';
import { RenfortsAConfirmer } from './RenfortsAConfirmer';

/**
 * Étape 3 de l'assistant d'import (maquette 26b) — ce qui sera écrit, et le choix en cas de conflit.
 *
 * Le planning ne bouge qu'ici. Tant que cette étape n'est pas validée, rien n'a été touché. Les renforts
 * extérieurs relevés y sont proposés ligne par ligne : seuls ceux que l'utilisateur coche sont créés.
 */
export function Etape3Ecriture({ bilan, mode, onMode, renforts, typesRenfort, onRenfort }: {
    bilan: BilanImport;
    mode: ModeEcriture;
    onMode: (mode: ModeEcriture) => void;
    renforts: RenfortPropose[];
    typesRenfort: string[];
    onRenfort: (index: number, patch: Partial<RenfortPropose>) => void;
}) {
    const lignes = [
        {
            n: bilan.creneaux, label: 'créneaux de service',
            detail: 'Recalculés depuis les débuts et fins, jamais depuis les totaux du fichier.',
            classe: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
        },
        {
            n: bilan.absences, label: 'absences datées',
            detail: 'REPOS, CP, AA, AM, RJF et leurs jours.',
            classe: 'bg-state-info-soft border-state-info/30 text-state-info',
        },
        {
            n: bilan.creneauxDeNuit, label: 'créneaux se terminant après minuit',
            detail: 'Fin après minuit, portée sur la journée de départ et comptée comme telle.',
            classe: 'bg-state-warn-soft border-state-warn/30 text-state-warn',
        },
        {
            n: bilan.codesCrees.length, label: 'codes d’absence créés',
            detail: bilan.codesCrees.length
                ? `${bilan.codesCrees.map((c) => c.code).join(', ')} n’existaient pas encore.`
                : 'Tous les codes du fichier existaient déjà.',
            classe: 'bg-state-warn-soft border-state-warn/30 text-state-warn',
        },
    ].filter((ligne) => ligne.n > 0 || ligne.label.startsWith('codes'));

    const choix: { valeur: ModeEcriture; titre: string; texte: string }[] = [
        {
            valeur: 'remplacer', titre: 'Remplacer la semaine entière',
            texte: `Les ${bilan.creneauxExistants} créneaux existants sont supprimés et remplacés par le fichier. À choisir si le fichier fait référence.`,
        },
        {
            valeur: 'completer', titre: 'Ne compléter que les jours vides',
            texte: 'Les créneaux déjà saisis dans l’IAmani sont conservés, le fichier ne comble que les trous.',
        },
    ];

    return (
        <div className="flex flex-col gap-[13px]">
            <div className="flex flex-col gap-[7px]">
                {lignes.map((ligne) => (
                    <div key={ligne.label} className={`flex items-center gap-[11px] px-3 py-2.5 rounded-[9px] border ${ligne.classe}`}>
                        <span className="min-w-[32px] text-base font-bold tabular-nums shrink-0">{ligne.n}</span>
                        <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold">{ligne.label}</div>
                            <div className="mt-px text-[11px] leading-snug opacity-80 text-pretty">{ligne.detail}</div>
                        </div>
                    </div>
                ))}
            </div>

            {bilan.creneauxExistants > 0 && (
                <div className="px-[13px] py-3 rounded-[10px] bg-state-danger-soft border border-state-danger/30">
                    <div className="text-[12.5px] font-bold text-state-danger">
                        Cette semaine contient déjà {bilan.creneauxExistants} créneaux dans l'IAmani
                    </div>
                    <div className="flex flex-col gap-[7px] mt-[9px]">
                        {choix.map((option) => (
                            <label key={option.valeur} className="flex items-start gap-2.5 cursor-pointer">
                                <input
                                    type="radio"
                                    name="mode-ecriture-import"
                                    className="sr-only"
                                    checked={mode === option.valeur}
                                    onChange={() => onMode(option.valeur)}
                                />
                                <span className={`w-[17px] h-[17px] shrink-0 mt-px rounded-full border-[1.5px] ${mode === option.valeur ? 'bg-state-danger border-state-danger' : 'bg-surface border-state-danger/40'}`} />
                                <span className="min-w-0">
                                    <span className="block text-[12.5px] font-semibold text-state-danger">{option.titre}</span>
                                    <span className="block mt-px text-[11.5px] leading-snug text-state-danger/90 text-pretty">{option.texte}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <RenfortsAConfirmer renforts={renforts} typesCatalogue={typesRenfort} onChange={onRenfort} />

            {bilan.nomsIgnores.length > 0 && (
                <div className="px-[13px] py-3 rounded-[10px] bg-surface-soft border border-border">
                    <p className="text-[11.5px] leading-relaxed text-content-muted text-pretty">
                        <strong className="text-content">{bilan.nomsIgnores.length} lignes ignorées</strong> : leur nom n'a pas été
                        reconnu. Rien n'est deviné — {bilan.nomsIgnores.slice(0, 3).join(', ')}
                        {bilan.nomsIgnores.length > 3 ? `, et ${bilan.nomsIgnores.length - 3} autres` : ''}.
                    </p>
                </div>
            )}

            <p className="text-xs leading-relaxed text-content-muted text-pretty">
                L'import est enregistré comme un lot, avec son fichier et son auteur : annulable en un clic pendant sept jours.
            </p>
        </div>
    );
}
